import { createSelector } from 'reselect';

import {
  selectCurrentLevel,
  selectCurrentDealerId,
} from 'app/dealer/shared/modules/dealersInsights/redux/level/selectors';
import { dealerInsightsDefaultCacheKey } from 'app/dealer/shared/modules/dealersInsights/api/cache';

import { getClosestIntegerSum } from '../../../../../shared/utils/pure-functions/calculations';
import { getLeadsBenchmarkReducer } from '../selectors';
import {
  selectLeadsBenchmarkSelectedYear,
  selectLeadsBenchmarkSelectedMonth,
} from '../general/selectors';
import { selectSelectedLeadsBenchmarkTimeDivisionFilter } from '../timeDivision/selectors';
import { leadsBenchmarkDataInitialState } from '../reducer.initialData';
import {
  intlLeadsBenchmarkTableRowFinancialInterestLabels,
  intlLeadsBenchmarkTableLabels,
} from '../../components/LeadsBenchmarkTable/intl/LeadsBenchmarkTable.intl';

import {
  LEADS_BENCHMARK_LEAD_TYPES,
  LEADS_BENCHMARKTABLE_TABLE,
  LEADS_PREFIX,
} from './constants';

// #region getLeadsBenchmarkCacheDictionaryKey
/**
 * @param {import('../reducer.defaultState').TimeDivision} selectedTimeDivisionFilter
 * @param {number} selectedYear
 * @param {number} selectedMonth
 * @param {import('../../../../../shared/modules/dealersInsights/redux/level/reducer.defaultState').DealerInsightsLevel} level
 * @param {string} dealerId
 */
export const getLeadsBenchmarkCacheDictionaryKey = (
  selectedTimeDivisionFilter,
  selectedYear,
  selectedMonth,
  level,
  dealerId,
) =>
  dealerInsightsDefaultCacheKey({
    featurePrefix: LEADS_PREFIX,
    selectedTimeDivisionFilter,
    selectedYear,
    selectedMonth,
    level,
    dealerId,
  });

// #endregion getLeadsBenchmarkCacheDictionaryKey

// #region select data by date and time division filters: main selector
/**
 * @description
 * main selector for selecting data corresponding to
 * cached dates in the redux store
 */
export const selectLeadsBenchmarkDataFromCache = createSelector(
  [
    getLeadsBenchmarkReducer,
    selectSelectedLeadsBenchmarkTimeDivisionFilter,
    selectLeadsBenchmarkSelectedYear,
    selectLeadsBenchmarkSelectedMonth,
    selectCurrentLevel,
    selectCurrentDealerId,
  ],
  (state, selectedTimeDivisionFilter, year, month, level, dealerId) =>
    state[
      getLeadsBenchmarkCacheDictionaryKey(
        selectedTimeDivisionFilter,
        year,
        month,
        level,
        dealerId,
      )
    ] || leadsBenchmarkDataInitialState,
);
// #endregion select data by date and time division filters: main selector

// #region data properties

// #region data properties: all previous and current
export const selectPreviousDataValues = createSelector(
  [selectLeadsBenchmarkDataFromCache],
  ({ previous }) => previous,
);

export const selectCurrentDataValues = createSelector(
  [selectLeadsBenchmarkDataFromCache],
  ({ current }) => current,
);
// #endregion data properties: all previous and current

// #region data properties: all are zero
export const selectAllLeadsBenchmarkDataValuesAreZero = createSelector(
  [selectPreviousDataValues, selectCurrentDataValues],
  (previous, current) => {
    return [...Object.values(previous), ...Object.values(current)].every(
      val => val === 0,
    );
  },
);
// #endregion data properties: all are zero

// #region data properties: phone
export const selectCurrentPhoneLeads = createSelector(
  [selectCurrentDataValues],
  ({ phoneLeads }) => {
    return phoneLeads;
  },
);

export const selectCurrentPhoneFinancialInterestLeads = createSelector(
  [selectCurrentDataValues],
  ({ phoneFinancialInterestLeads }) => phoneFinancialInterestLeads,
);

export const selectPreviousPhoneLeads = createSelector(
  [selectPreviousDataValues],
  ({ phoneLeads }) => phoneLeads,
);

export const selectPreviousPhoneFinancialInterestLeads = createSelector(
  [selectPreviousDataValues],
  ({ phoneFinancialInterestLeads }) => phoneFinancialInterestLeads,
);
// #endregion

// #region data properties: email
export const selectCurrentEmailLeads = createSelector(
  [selectCurrentDataValues],
  ({ emailLeads }) => emailLeads,
);

export const selectCurrentEmailFinancialInterestLeads = createSelector(
  [selectCurrentDataValues],
  ({ emailFinancialInterestLeads }) => emailFinancialInterestLeads,
);

export const selectPreviousEmailLeads = createSelector(
  [selectPreviousDataValues],
  ({ emailLeads }) => emailLeads,
);

export const selectPreviousEmailFinancialInterestLeads = createSelector(
  [selectPreviousDataValues],
  ({ emailFinancialInterestLeads }) => emailFinancialInterestLeads,
);
// #endregion data properties: email

// #region data properties: chat
export const selectCurrentChatLeads = createSelector(
  [selectCurrentDataValues],
  ({ chatLeads }) => chatLeads,
);

export const selectCurrentChatFinancialInterestLeads = createSelector(
  [selectCurrentDataValues],
  ({ chatFinancialInterestLeads }) => chatFinancialInterestLeads,
);

export const selectPreviousChatLeads = createSelector(
  [selectPreviousDataValues],
  ({ chatLeads }) => chatLeads,
);

export const selectPreviousChatFinancialInterestLeads = createSelector(
  [selectPreviousDataValues],
  ({ chatFinancialInterestLeads }) => chatFinancialInterestLeads,
);
// #endregion data properties: chat

// #endregion data properties

// #region response data calculations

// #region response data calculations: total leads
export const selectCurrentTotalLeads = createSelector(
  [selectCurrentPhoneLeads, selectCurrentEmailLeads, selectCurrentChatLeads],
  (phone, email, chat) => getClosestIntegerSum(phone, email, chat),
);

export const selectPreviousTotalLeads = createSelector(
  [selectPreviousPhoneLeads, selectPreviousEmailLeads, selectPreviousChatLeads],
  (phone, email, chat) => getClosestIntegerSum(phone, email, chat),
);

export const selectCurrentTotalFinancialInterestLeads = createSelector(
  [
    selectCurrentPhoneFinancialInterestLeads,
    selectCurrentEmailFinancialInterestLeads,
    selectCurrentChatFinancialInterestLeads,
  ],
  (phone, email, chat) => getClosestIntegerSum(phone, email, chat),
);

export const selectPreviousTotalFinancialInterestLeads = createSelector(
  [
    selectPreviousPhoneFinancialInterestLeads,
    selectPreviousEmailFinancialInterestLeads,
    selectPreviousChatFinancialInterestLeads,
  ],
  (phone, email, chat) => getClosestIntegerSum(phone, email, chat),
);
// #endregion response data calculations: total leads

// #region response data calculations: total leads change
export const selectTotalLeadsChange = createSelector(
  [selectCurrentTotalLeads, selectPreviousTotalLeads],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);

export const selectTotalFinancialInterestLeadsChange = createSelector(
  [
    selectCurrentTotalFinancialInterestLeads,
    selectPreviousTotalFinancialInterestLeads,
  ],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);
// #endregion response data calculations: total leads change

// #region response data calculations: phone leads change
export const selectPhoneLeadsChange = createSelector(
  [selectCurrentPhoneLeads, selectPreviousPhoneLeads],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);

export const selectPhoneFinancialInterestLeadsChange = createSelector(
  [
    selectCurrentPhoneFinancialInterestLeads,
    selectPreviousPhoneFinancialInterestLeads,
  ],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);
// #endregion response data calculations: phone leads change

// #region response data calculations: email leads change
export const selectEmailLeadsChange = createSelector(
  [selectCurrentEmailLeads, selectPreviousEmailLeads],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);

export const selectEmailFinancialInterestLeadsChange = createSelector(
  [
    selectCurrentEmailFinancialInterestLeads,
    selectPreviousEmailFinancialInterestLeads,
  ],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);
// #endregion response data calculations: email leads change

// #region response data calculations: chat leads change
export const selectChatLeadsChange = createSelector(
  [selectCurrentChatLeads, selectPreviousChatLeads],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);

export const selectChatFinancialInterestLeadsChange = createSelector(
  [
    selectCurrentChatFinancialInterestLeads,
    selectPreviousChatFinancialInterestLeads,
  ],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);
// #endregion response data calculations: chat leads change

// #endregion response data calculations

// #region table dictionary selectors

// #region table dictionary selectors: getLeadsTableData
export function getLeadsTableData(intlKey, data) {
  return {
    [LEADS_BENCHMARKTABLE_TABLE.LABEL]: intlLeadsBenchmarkTableLabels[intlKey],
    [LEADS_BENCHMARKTABLE_TABLE.FINANCIAL_INTEREST_LABEL]:
      intlLeadsBenchmarkTableRowFinancialInterestLabels[intlKey],
    [LEADS_BENCHMARKTABLE_TABLE.LEADS_COUNT]: data.leads,
    [LEADS_BENCHMARKTABLE_TABLE.LEADS_COUNT_CHANGE]: data.leadsChange,
    [LEADS_BENCHMARKTABLE_TABLE.LEADS_FINANCIAL_INTEREST_COUNT]:
      data.financialInterestLeads,
    [LEADS_BENCHMARKTABLE_TABLE.LEADS_FINANCIAL_INTEREST_COUNT_CHANGE]:
      data.financialInterestLeadsChange,
  };
}
// #endregion table dictionary selectors: getLeadsTableData

// #region table dictionary selectors: selectTotalLeadsDictionary
export const selectTotalLeadsDictionary = createSelector(
  [
    selectCurrentTotalLeads,
    selectTotalLeadsChange,
    selectCurrentTotalFinancialInterestLeads,
    selectTotalFinancialInterestLeadsChange,
  ],
  (leads, leadsChange, financialInterestLeads, financialInterestLeadsChange) =>
    getLeadsTableData(LEADS_BENCHMARK_LEAD_TYPES.TOTAL, {
      leads,
      leadsChange,
      financialInterestLeads,
      financialInterestLeadsChange,
    }),
);
// #endregion table dictionary selectors: selectTotalLeadsDictionary

// #region table dictionary selectors: selectPhoneLeadsDictionary
export const selectPhoneLeadsDictionary = createSelector(
  [
    selectCurrentPhoneLeads,
    selectPhoneLeadsChange,
    selectCurrentPhoneFinancialInterestLeads,
    selectPhoneFinancialInterestLeadsChange,
  ],
  (leads, leadsChange, financialInterestLeads, financialInterestLeadsChange) =>
    getLeadsTableData(LEADS_BENCHMARK_LEAD_TYPES.PHONE, {
      leads,
      leadsChange,
      financialInterestLeads,
      financialInterestLeadsChange,
    }),
);
// #endregion table dictionary selectors: selectPhoneLeadsDictionary

// #region table dictionary selectors: selectEmailLeadsDictionary
export const selectEmailLeadsDictionary = createSelector(
  [
    selectCurrentEmailLeads,
    selectEmailLeadsChange,
    selectCurrentEmailFinancialInterestLeads,
    selectEmailFinancialInterestLeadsChange,
  ],
  (leads, leadsChange, financialInterestLeads, financialInterestLeadsChange) =>
    getLeadsTableData(LEADS_BENCHMARK_LEAD_TYPES.EMAIL, {
      leads,
      leadsChange,
      financialInterestLeads,
      financialInterestLeadsChange,
    }),
);
// #endregion table dictionary selectors: selectEmailLeadsDictionary

// #region table dictionary selectors: selectChatLeadsDictionary
export const selectChatLeadsDictionary = createSelector(
  [
    selectCurrentChatLeads,
    selectChatLeadsChange,
    selectCurrentChatFinancialInterestLeads,
    selectChatFinancialInterestLeadsChange,
  ],
  (leads, leadsChange, financialInterestLeads, financialInterestLeadsChange) =>
    getLeadsTableData(LEADS_BENCHMARK_LEAD_TYPES.CHAT, {
      leads,
      leadsChange,
      financialInterestLeads,
      financialInterestLeadsChange,
    }),
);
// #endregion table dictionary selectors: selectChatLeadsDictionary

// #region table dictionary selectors: table data

/**
 * @typedef {Object} LeadsBenchmarkTableIntlLabel
 * @property {string} id
 * @property {string} defaultMessage
 * @property {Object} styles
 * @property {hideOnMobile} boolean
 */

/**
 * @typedef {Object} LeadsBenchmarkTableDictionaryValues
 * @property {LeadsBenchmarkTableIntlLabel} label
 * @property {LeadsBenchmarkTableIntlLabel} financialInterestLabel
 * @property {number} leadsCount
 * @property {number} leadsCountChange
 * @property {number} leadsFinancialInterestCount
 * @property {number} leadsFinancialInterestCountChange
 */

/**
 * @note string key is enum: import('./constants').LeadsType
 * @typedef {Object<string, LeadsBenchmarkTableDictionaryValues>} LeadsBenchmarkTableDictionary
 */

/**
 * @returns {LeadsBenchmarkTableDictionary}
 */
export const selectTableDataDictionary = createSelector(
  [
    selectTotalLeadsDictionary,
    selectPhoneLeadsDictionary,
    selectEmailLeadsDictionary,
    selectChatLeadsDictionary,
  ],
  (total, phone, email, chat) => ({ total, phone, email, chat }),
);
// #endregion table dictionary selectors: table data

// #endregion table dictionary selectors
